import { Form, Input, Select, Switch, Button, message, Row, Col } from "antd";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useFormFieldDetail,
  useFormFieldList,
  useFormFieldSave,
} from "../../../hooks/api/dynamic-form/hooks";
import {
  FIELD_TYPES,
  FIELD_TYPES_WITH_OPTIONS,
  FormFieldFormData,
  FormFieldProps,
} from "../../../types/api/dynamic-form";
import { useEffect, useMemo } from "react";
import { removeSpecialChars } from "../../../lib/utils";
import BackButton from "../../components/reuseComp/BackButton";
import { useParams } from "react-router-dom";
import { formFieldValidation } from "../../components/validations";
import { useMessageApi } from "../../../provider/AntdNotification";

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  required = false,
  control,
  children,
}) => (
  <Form.Item label={label} required={required} className="w-full">
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div>
          {children(field)}
          {fieldState.error && (
            <p className="text-red-500 text-xs mt-1">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  </Form.Item>
);

const CreateFormFields = () => {
  const { id } = useParams<{ id: string }>();
  const messageApi = useMessageApi()

  const { data: details } = useFormFieldDetail(id || "");
  const { data: formList } = useFormFieldList({ isAll: true });

  const { mutate: saveForm, isPending } = useFormFieldSave({
    onSuccess: () =>
      messageApi.success(`Field ${id ? "updated" : "created"} successfully!`),
    onError: (error: any) => {
      messageApi.error(error.message || "Something went wrong");
    },
  });

  // Default values
  const defaultValues: FormFieldFormData = {
    label: "",
    fieldId: "",
    status: true,
    type: "",
    placeholder: "",
    helpText: "",
    required: false,
    order: 0,
    options: [],
    errorMessage: "",
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormFieldFormData>({
    resolver: yupResolver(formFieldValidation) as any,
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "options",
  });


  const [type, requiredField, showIfValue, labelValue] = watch([
    "type",
    "required",
    "showIf.fieldId",
    "label",
  ]);

  const hasOptions = useMemo(
    () => FIELD_TYPES_WITH_OPTIONS.includes(type),
    [type]
  );

  const showIfFields = useMemo(
    () => formList?.data?.filter((field: any) => field._id !== id) || [],
    [formList, id]
  );

  useEffect(() => {
    if (labelValue) {
      const generatedId = removeSpecialChars(labelValue);
      setValue("fieldId", generatedId);
    }
  }, [labelValue]);

  useEffect(() => {
    if (details?.data?._id) {
      const formData: FormFieldFormData = {
        label: details.data.label || "",
        fieldId: details.data.fieldId || "",
        type: details.data.type || "",
        placeholder: details.data.placeholder || "",
        helpText: details.data.helpText || "",
        required: details.data.required || false,
        order: details.data.order,
        options: details.data.options || [],
        status: details.data.status || false,
        errorMessage: details.data.errorMessage || "",
        showIf: details.data.showIf || { fieldId: "", value: null },
      };
      reset(formData);
    }
  }, [details, reset]);

  useEffect(() => {
    if (!showIfValue) {
      setValue("showIf.value", null);
    }
  }, [showIfValue, setValue]);

  const handleAddOption = () => {
    if (type === "checkbox") {
      append({ label: "", value: false });
    } else {
      append({ label: "", value: "" });
    }
  };

  const onSubmit = (data: FormFieldFormData) => {
    const formData = {
      ...data,
      ...(id && { _id: id }),
      // Only include showIf if fieldId is set
      showIf: data.showIf?.fieldId ? data.showIf : undefined,
    };

    saveForm(formData as any);
  };

  return (
    <div className="p-4">

      <BackButton>Back</BackButton>

      <h1 className="text-xl sm:text-3xl font-semibold mb-6">
        {id ? "Edit Field" : "Create Field"}
      </h1>

      <div className="p-6 bg-[#FFEFE5] rounded-md">
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Row gutter={[16, 16]}>
            {/* Basic Information */}
            <Col xs={24} md={24}>
              <FormField label="Label" name="label" required control={control}>
                {(field) => <Input {...field} placeholder="Field Label" />}
              </FormField>
            </Col>

            <Col xs={24} md={12}>
              <FormField
                label="Field ID"
                name="fieldId"
                required
                control={control}
              >
                {(field) => (
                  <Input
                    {...field}
                    placeholder="Auto-generated field identifier"
                  />
                )}
              </FormField>
            </Col>

            <Col xs={24} md={12}>
              <FormField label="Index" name="order" required control={control}>
                {(field) => (
                  <Input
                    {...field}
                    type="Number"
                    placeholder="Enter Indexing"
                  />
                )}
              </FormField>
            </Col>

            <Col xs={24} md={12}>
              <FormField
                label="Field Type"
                name="type"
                required
                control={control}
              >
                {(field) => (
                  <Select
                    {...field}
                    allowClear
                    className="w-full"
                    placeholder="Select Field Type"
                    options={FIELD_TYPES}
                  />
                )}
              </FormField>
            </Col>

            <Col xs={24} md={4}>
              <FormField label="Required" name="required" control={control}>
                {(field) => (
                  <Switch checked={field.value} onChange={field.onChange} />
                )}
              </FormField>
            </Col>

            <Col xs={24} md={6}>
              <FormField
                label="Status"
                name="status"
                required
                control={control}
              >
                {(field) => (
                  <Select
                    {...field}
                    allowClear
                    className="w-full"
                    placeholder="Please Select Status"
                    options={[
                      { label: "Active", value: true },
                      { label: "Inactive", value: false },
                    ]}
                  />
                )}
              </FormField>
            </Col>

            {requiredField && (
              <Col xs={24}>
                <FormField
                  label="Error Message"
                  name="errorMessage"
                  required
                  control={control}
                >
                  {(field) => (
                    <Input {...field} placeholder="Custom validation message" />
                  )}
                </FormField>
              </Col>
            )}

            {/* Additional Fields */}
            <Col xs={24} md={12}>
              <FormField
                label="Placeholder"
                name="placeholder"
                control={control}
              >
                {(field) => <Input {...field} placeholder="Placeholder text" />}
              </FormField>
            </Col>

            <Col xs={24} md={12}>
              <FormField label="Help Text" name="helpText" control={control}>
                {(field) => (
                  <Input {...field} placeholder="Help text for users" />
                )}
              </FormField>
            </Col>

            {/* Options for select, radio, checkbox */}
            {hasOptions && (
              <Col xs={24}>
                <div className="mb-4">
                  <p className="font-medium mb-3">Options</p>

                  {fields.map((field, index) => (
                    <Row key={field.id} gutter={[8, 8]} className="mb-3">
                      <Col xs={12} md={10}>
                        <Controller
                          name={`options.${index}.label`}
                          control={control}
                          render={({ field: optionField }) => (
                            <Input
                              placeholder="Option label"
                              {...optionField}
                            />
                          )}
                        />
                      </Col>

                      <Col xs={10} md={10}>
                        <Controller
                          name={`options.${index}.value`}
                          control={control}
                          render={({ field: optionField }) => {
                            if (type === "checkbox") {
                              return (
                                <Switch
                                  checked={Boolean(optionField.value)}
                                  onChange={optionField.onChange}
                                />
                              );
                            }
                            return (
                              <Input
                                placeholder="Value"
                                value={optionField.value as string}
                                onChange={optionField.onChange}
                              />
                            );
                          }}
                        />
                      </Col>

                      <Col xs={2} md={4}>
                        <Button
                          danger
                          onClick={() => remove(index)}
                          className="w-full"
                        >
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}

                  <Button
                    type="dashed"
                    block
                    onClick={handleAddOption}
                    className="mt-2"
                  >
                    + Add Option
                  </Button>
                </div>
              </Col>
            )}

            {/* Conditional Display Logic */}
            {showIfFields.length > 0 && (
              <>
                <Col span={24}>
                  <p className="font-medium mb-2">Conditional Display</p>
                  <p className="text-sm text-gray-600 mb-3">
                    Show this field only when another field has a specific value
                  </p>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="Show when field">
                    <Controller
                      name="showIf.fieldId"
                      control={control}
                      render={({ field, fieldState }) => (
                        <div>
                          <Select
                            {...field}
                            allowClear
                            className="w-full"
                            placeholder="Select field"
                            options={showIfFields.map((field: any) => ({
                              label: field.label,
                              value: field._id,
                            }))}
                          />
                          {fieldState.error && (
                            <p className="text-red-500 text-xs mt-1">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  {showIfValue && (
                    <Form.Item label="Has value">
                      <Controller
                        name="showIf.value"
                        control={control}
                        render={({ field, fieldState }) => (
                          <div>
                            <Input placeholder="Value to match" {...field} />
                            {fieldState.error && (
                              <p className="text-red-500 text-xs mt-1">
                                {fieldState.error.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </Form.Item>
                  )}
                </Col>
              </>
            )}
          </Row>

          <div className="mt-8">
            <Button
              type="primary"
              htmlType="submit"
              loading={isPending}
              size="large"
              className="w-full sm:w-auto"
            >
              {id ? "Update Field" : "Create Field"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CreateFormFields;
